/* eslint-disable camelcase */
/**
 * Inline chatbot element, e.g. placed next to the video player. Includes a mobile trigger dialog.
 */
import * as Sentry from '@sentry/browser';
import { parse } from 'marked';

import { buildURL, fetchRequest } from 'Utils/api-fetch-request';
import { fadeInGroups } from 'Utils/fade-in';

import {
    AIChatbotAssistantType,
    AIChatbotCollapseTrigger,
    AIChatbotEvents,
    AIChatbotQuestionSource,
    trackAIChatbotEvent,
} from 'Analytics/ai-chatbot-events';

import VuMediChatbotBase from './base';
import MockChatbotEventSource from './mock-chatbot-event-source';

const Copy = {
    cta: 'Save time: Ask for the main points',
    placeholder: {
        default: 'Ask about this video',
        loading: 'Searching...',
    },
};

const _CTA_QUESTION = {
    id: 31,
    question: 'Ask your own question',
    type: 'cta',
};

const _MOBILE_BREAKPOINT = 992;
const _DESKTOP_AUTO_COLLAPSE_TIMEOUT = 5000;
const _DESKTOP_COLLAPSE_DURATION = 300;
const _SUMMARIZE_VIDEO_QUESTION = 'Summarize this video';

/**
 * Map question type to question source property for Mixpanel events.
 */
const _getQuestionSource = (type) => {
    const sourceMap = {
        predefined: AIChatbotQuestionSource.PREDEFINED_QUESTION,
        next_best_question: AIChatbotQuestionSource.NEXT_BEST_QUESTION,
        user_question: AIChatbotQuestionSource.CUSTOM_QUESTION,
    };
    return sourceMap[type] || null;
};

export default class VuMediChatbotInline extends VuMediChatbotBase {
    constructor(videoId, viewedUUID = null, brightcovePlayer = null, videoSlug = null, clientShowProductTour = false) {
        super(videoSlug);

        this.videoId = videoId;
        this.viewedUUID = viewedUUID;
        this.brightcovePlayer = brightcovePlayer;
        this.clientShowProductTour = clientShowProductTour;

        this.playerAnchor = null;
        this.mobileTriggerElement = null;
        this.suggestionsElement = null;

        this.resizeDebouncer = null;

        this.isPeekaboo = false;
        this.peekaboo = {
            summary: null,
            nbq: null,
        };

        this.skipAnimations = false;
        this.showProductTour = false;

        this.eventAssistantType = AIChatbotAssistantType.VIDEO;
    }

    _animateDesktopAssistant() {
        const fadeInParent = this.chatbotElement.querySelector('.vm-chatbot-placeholder');
        const fadeInController = fadeInGroups(fadeInParent, this.skipAnimations ? 0 : 1000);

        window.setTimeout(
            () => {
                this.chatbotElement.setAttribute('data-collapsed', 'false');

                trackAIChatbotEvent(AIChatbotEvents.EXPANDED, {
                    assistant_type: this.eventAssistantType,
                    trigger: AIChatbotCollapseTrigger.AUTO,
                    is_peekaboo: this.isPeekabooVersion,
                });

                window.setTimeout(
                    () => {
                        fadeInController.show();
                    },
                    this.skipAnimations ? 0 : _DESKTOP_COLLAPSE_DURATION,
                );
            },
            this.skipAnimations ? 0 : _DESKTOP_AUTO_COLLAPSE_TIMEOUT,
        );
    }

    _handleStreamingAnswerResponse(answerElement, question, type = '', isPeekabooQuestion = false) {
        return new Promise((resolve) => {
            if (!this.uuid) {
                resolve();
                return;
            }

            const videoTimestampMs = Math.round(this.brightcovePlayer?._player.currentTime() * 1000);
            const paramQuestion = `question=${encodeURIComponent(question)}`;
            const paramSource = `question_source=${encodeURIComponent(type)}`;
            const paramTimestamp = `video_timestamp_ms=${videoTimestampMs}`;
            const paramPeekaboo = `${isPeekabooQuestion ? '&peek_a_boo_extended=true' : ''}`;
            const eventSourceURL = `/api/chats/${this.uuid}/ask/?${paramQuestion}&${paramSource}&${paramTimestamp}${paramPeekaboo}`;

            this.activeEventSource = new EventSource(eventSourceURL);

            const mockEventSource = isPeekabooQuestion
                ? new MockChatbotEventSource(this.peekaboo.summary, this.peekaboo.nbq)
                : null;

            /* In case `isPeekabooQuestion` is set to true - peekaboo summary should be showed - we need to call
             * `this._handleEventSource` twice.
             *
             * 1. The first call should hit the backend and say this is a peekaboo call - the backend will create a record
             *    for analytics purposes.
             * 2. The second call is using MockEventSource instance that actually renders the answer to the user. This mock
             *    calls enables us to use the same method for both "fake" streaming response and a real streaming response.
             */
            Promise.all([
                this._handleEventSource(this.activeEventSource, answerElement, isPeekabooQuestion),
                isPeekabooQuestion ? this._handleEventSource(mockEventSource, answerElement) : null,
            ]).then(() => resolve());
        });
    }

    async _askQuestion(question, type = '', questionClickSource = '') {
        if (!this.chatbotElement) {
            return;
        }

        const bodyElement = this.chatbotElement.querySelector('.vm-chatbot-body');
        const inputElement = this.chatbotElement.querySelector('.vm-chatbot-footer input[type="text"]');
        const submitElement = this.chatbotElement.querySelector('.vm-chatbot-footer button[type="submit"]');

        if (questionClickSource === 'mobile') {
            trackAIChatbotEvent(AIChatbotEvents.OPENED, {
                assistant_type: this.eventAssistantType,
            });

            document.body.style.overflow = 'hidden';
            this.chatbotElement.classList.add('vm-chatbot-video-assistant--mobile-in');
            this.brightcovePlayer.setFixedLayout(true);

            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });

            window.setTimeout(() => {
                this._mobileSetStyles();
            }, 0);
        }

        this.chatbotElement.classList.add('vm-chatbot-video-assistant--conversation');
        inputElement.value = '';
        bodyElement.classList.add('vm-chatbot-body--conversation');

        if (type === 'cta') {
            inputElement.focus();
            return;
        }

        submitElement.setAttribute('disabled', 'disabled');
        inputElement.setAttribute('placeholder', Copy.placeholder.loading);

        trackAIChatbotEvent(AIChatbotEvents.QUESTION_SUBMITTED, {
            assistant_type: this.eventAssistantType,
            question_source: _getQuestionSource(type),
            question,
            is_peekaboo: this.isPeekabooVersion,
        });

        this._renderQuestion(question);
        this._toggleSkeleton(true);
        this.inProgress = true;

        const answerElement = this._answerPrepareElement();

        if (!this.uuid) {
            await this._initializeSession();
        }

        const isPeekabooQuestion = question === _SUMMARIZE_VIDEO_QUESTION && this.isPeekaboo;

        await this._handleStreamingAnswerResponse(answerElement, question, type, isPeekabooQuestion);

        submitElement.removeAttribute('disabled');
        inputElement.setAttribute('placeholder', Copy.placeholder.default);

        this.inProgress = false;
        this._toggleSkeleton(false);
    }

    _initializeChatbot() {
        return new Promise((resolve, reject) => {
            const params = {
                content_type: 'video',
                object_id: this.videoId,
            };

            const requestURL = buildURL('/api/chats/chat-configuration/', params);

            fetchRequest(requestURL)
                .then((configuration) => {
                    if (configuration?.show_product_tour && this.clientShowProductTour) {
                        this.skipAnimations = true;
                        this.showProductTour = true;
                    }
                    if (!configuration) {
                        reject();
                        return;
                    }

                    resolve({
                        predefined_questions: configuration.predefined_questions ?? [],
                        video_summary: configuration.video_summary,
                        peekaboo_enabled: configuration.peekaboo_enabled,
                    });
                })
                .catch(() => {
                    reject();
                });
        });
    }

    _initializeHead() {
        const metaElement = document.createElement('meta');

        metaElement.setAttribute('name', 'theme-color');
        metaElement.setAttribute('content', '#444444');

        document.head.appendChild(metaElement);
    }

    _initializeSession() {
        return new Promise((resolve, reject) => {
            const options = {
                method: 'POST',
                body: JSON.stringify({
                    content_type: 'video',
                    object_id: this.videoId,
                    viewed_uuid: this.viewedUUID,
                }),
            };

            fetchRequest('/api/chats/', options)
                .then((data) => {
                    if (!data?.uuid) {
                        reject();
                        Sentry.captureException(new Error('Invalid response from the server'));
                        return;
                    }

                    this.uuid = data.uuid;
                    resolve();
                })
                .catch((error) => {
                    reject();
                    Sentry.captureException(error);
                });
        });
    }

    _renderChatbotElement(predefinedQuestions) {
        /*
            PEEK-A-BOO SUMMARY CHANGE
            we show the peek-a-boo summary only on desktop
            and we use a <div> element instead of a <button> for the question
        */

        const peekabooClasses = () => {
            return this.isPeekaboo ? 'vm-chatbot-video-assistant--peekaboo js_chatbotVideoAssistantPeekaboo' : '';
        };
        const elementType = this.isPeekaboo ? 'div' : 'button';
        const predefinedQuestionsMarkup = predefinedQuestions
            .map((q, index) =>
                this._getSuggestionMarkup(
                    index + 1,
                    q.type,
                    q.question,
                    'js_chatbotPlaceholderSuggestion',
                    index + 1,
                    elementType,
                ),
            )
            .join('');

        const markup = `<div class="vm-chatbot-video-assistant ${peekabooClasses()}" data-collapsed="true">
            <div class="vm-chatbot-header">
                ${this._vumediAiLogo()}
                <button type="button" class="vm-button vm-button--md vm-button--text-only vm-button--chatbot-hide js_chatbotHide">
                    Hide
                </button>
                <button type="button" class="vm-button vm-button--md vm-button--chatbot-collapse js_chatbotCollapse">
                    <i class="icon icon-chevron-down"></i>
                </button>
            </div>
            <div class="vm-chatbot-body js_chatbotBody">
                <div class="vm-chatbot-placeholder">
                    <h4 class="vm-chatbot-placeholder__title">${Copy.cta}</h4>
                    <div class="vm-chatbot-placeholder__suggestions js_chatbotPlaceholderSuggestions">
                        ${predefinedQuestionsMarkup}
                    </div>
                </div>
                <div class="vm-chatbot-messages js_vmChatbotMessages">
                    <vm-chatbot-skeleton data-hidden data-type="inline"></vm-chatbot-skeleton>
                    <div class="js_vmChatbotSpacer"></div>
                </div>
            </div>
            <div class="vm-chatbot-footer">
                <form class="vm-chatbot-footer__form">
                    <i class="icon icon-chatbot"></i>
                    <input type="text"
                        name="question"
                        class="vm-chatbot-input"
                        autocomplete="off"
                        placeholder="${Copy.placeholder.default}"
                        data-type="user_question"
                        aria-label="Question for the AI" />
                    <button type="submit" class="vm-button vm-button--sm vm-button--primary vm-button--chatbot-send">
                        <i class="icon icon-arrow-up"></i>
                    </button>
                </form>
            </div>
        </div>`;

        const template = document.createElement('template');
        template.innerHTML = markup;

        const containerElement = template.content.firstChild;
        const questionForm = containerElement.querySelector('form');
        const inputElement = questionForm.querySelector('input');

        inputElement.addEventListener('keydown', (ev) => {
            ev.stopPropagation();
        });

        questionForm.addEventListener('submit', (ev) => {
            ev.preventDefault();

            if (this.inProgress) {
                return;
            }

            const question = inputElement.value.trim();
            inputElement.value = '';

            if (!question) {
                return;
            }

            inputElement.blur();
            this._askQuestion(question, inputElement.getAttribute('data-type') || '');
        });

        containerElement.querySelector('.js_chatbotCollapse').addEventListener('click', (ev) => {
            ev.preventDefault();
            const state = containerElement.getAttribute('data-collapsed');
            const newState = state === 'true' ? 'false' : 'true';
            containerElement.setAttribute('data-collapsed', newState);

            trackAIChatbotEvent(newState === 'false' ? AIChatbotEvents.EXPANDED : AIChatbotEvents.COLLAPSED, {
                assistant_type: this.eventAssistantType,
                trigger: AIChatbotCollapseTrigger.MANUAL,
                is_peekaboo: this.isPeekabooVersion,
            });
        });

        containerElement.querySelector('.js_chatbotHide').addEventListener('click', (ev) => {
            ev.preventDefault();
            document.body.style.overflow = '';
            containerElement.classList.remove('vm-chatbot-video-assistant--mobile-in');
            this.brightcovePlayer.unsetFixedLayout(true);

            window.scrollTo({
                top: 0,
                behavior: 'instant',
            });

            if (this.activeEventSource) {
                try {
                    this.activeEventSource.close();
                    this.activeEventSource = null;
                    this.inProgress = false;
                } catch (error) {
                    Sentry.captureException(error);
                }
            }
        });

        this._dynamicHeightObserver(containerElement);

        return containerElement;
    }

    _renderChatbotMobileTriggerElement(predefinedQuestions = []) {
        const questionsMarkup = [...predefinedQuestions, _CTA_QUESTION]
            .map((q, index) =>
                this._getSuggestionMarkup(index + 1, q.type, q.question, 'js_chatbotPlaceholderSuggestion'),
            )
            .join('');

        const markup = `<div class="vm-chatbot-mobile-trigger" data-source="mobile">
            <div class="vm-chatbot-placeholder__suggestions-container">
                <div class="vm-chatbot-placeholder__suggestions js_chatbotPlaceholderSuggestions">
                    <i class="icon icon-chatbot"></i>
                    ${questionsMarkup}
                </div>
            </div>
        </div>`;

        const template = document.createElement('template');
        template.innerHTML = markup;

        return template.content.firstChild;
    }

    async _render(container, predefinedQuestions = []) {
        this.chatbotElement = this._renderChatbotElement(predefinedQuestions);
        container.appendChild(this.chatbotElement);

        /*
            PEEK-A-BOO SUMMARY CHANGE
            render the peek-a-boo summary layout if the container has the js_chatbotVideoAssistantPeekaboo class
        */

        if (this.isPeekaboo && predefinedQuestions.length) {
            await this._renderPeekabooSummary(container, predefinedQuestions[0].question, predefinedQuestions[0].type);
        }

        this._responsiveHandler();
        this._placeholderSuggestionsClickHandler(this.chatbotElement);
    }

    _renderMobileTrigger(container, predefinedQuestions = []) {
        this.mobileTriggerElement = this._renderChatbotMobileTriggerElement(predefinedQuestions);
        container.appendChild(this.mobileTriggerElement);

        this._placeholderSuggestionsClickHandler(this.mobileTriggerElement);
    }

    _mobileSetStyles() {
        const rect = this.playerAnchor.getBoundingClientRect();
        const cssTop = `${rect.height}px`;
        const cssHeight = `calc(100dvh - ${cssTop})`;

        this.chatbotElement.style.setProperty('top', cssTop);
        this.chatbotElement.style.setProperty('height', cssHeight);
    }

    _mobileClearStyles() {
        this.brightcovePlayer.unsetFixedLayout(true);
        this.chatbotElement.style.removeProperty('top');
        this.chatbotElement.style.removeProperty('height');
        this.chatbotElement.classList.remove('vm-chatbot-video-assistant--mobile-in');
        document.body.style.overflow = '';
    }

    _responsiveHandler() {
        this.playerAnchor = this.brightcovePlayer.getPlayerElement();

        if (!this.playerAnchor) {
            return;
        }

        if (window.innerWidth < _MOBILE_BREAKPOINT) {
            trackAIChatbotEvent(AIChatbotEvents.LOADED, {
                assistant_type: this.eventAssistantType,
            });
            this.chatbotElement.setAttribute('data-collapsed', 'false');
            this._mobileSetStyles();
        } else {
            trackAIChatbotEvent(AIChatbotEvents.LOADED, {
                assistant_type: this.eventAssistantType,
                is_peekaboo: this.isPeekabooVersion,
            });
            this._animateDesktopAssistant();
        }

        window.addEventListener('resize', () => {
            window.clearTimeout(this.resizeDebouncer);
            this.resizeDebouncer = null;

            this.resizeDebouncer = window.setTimeout(() => {
                if (window.innerWidth < _MOBILE_BREAKPOINT) {
                    this.chatbotElement.setAttribute('data-collapsed', 'false');
                    this._mobileSetStyles();
                } else {
                    this._mobileClearStyles();
                }
            }, 100);
        });
    }

    _renderPeekabooSummary = async (container) => {
        container.querySelector('.js_chatbotPlaceholderSuggestion').innerHTML = `
            <h3>Video Summary</h3>
            ${parse(this.peekaboo.summary)}
            <div class="vm-button-container">
                <span class="vm-button vm-button--md vm-button--show-full-summary">Show full summary</span>
            </div>
        `;

        return;
    };

    async initialize(container, mobileTriggerContainer) {
        try {
            const { predefined_questions, video_summary, peekaboo_enabled } = await this._initializeChatbot();

            this.isPeekaboo = peekaboo_enabled && video_summary && window.innerWidth >= _MOBILE_BREAKPOINT;
            this.peekaboo.summary = video_summary;
            this.peekaboo.nbq = predefined_questions.filter((q) => q.question !== _SUMMARIZE_VIDEO_QUESTION);

            this._initializeHead();

            this._render(container, predefined_questions);
            this._renderMobileTrigger(mobileTriggerContainer, predefined_questions);

            container.classList.remove('hidden');
            mobileTriggerContainer.classList.remove('hidden');
            return true;
        } catch {
            return false;
        }
    }
}
